"use client"

import * as React from "react"

import { DirectionProvider } from "@radix-ui/react-direction"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { NextIntlClientProvider } from "next-intl"

import type { Customer, GoogleTag as GoogleTagConfig } from "@workspace/model"

import { AnalyticsProvider } from "@/components/analytics/analytics-provider"
import { ConsentBanner } from "@/components/analytics/consent-banner"
import { GoogleTag } from "@/components/analytics/google-tag"
import { AuthProvider } from "@/components/auth-provider"
import { CartProvider } from "@/components/cart-provider"
import {
  StoreConfigProvider,
  type StoreConfigContextValue,
} from "@/components/store-config-provider"
import { ThemeProvider } from "@/components/theme-provider"

/**
 * The storefront provider tree (US-011), ported from retail in this nesting order:
 *
 *   ThemeProvider → QueryClientProvider → DirectionProvider →
 *   NextIntlClientProvider → StoreConfigProvider → AnalyticsProvider →
 *   AuthProvider → CartProvider
 *
 * AnalyticsProvider also mounts the tenant's Google tag bootstrap and the
 * consent banner (both render nothing when `googleTag` is null).
 *
 * next-intl auto-inherits locale/messages only in Server Components; because this
 * is a Client Component the root layout passes `locale` + `messages` explicitly.
 */
export function Providers({
  children,
  locale,
  direction,
  messages,
  storeConfig,
  googleTag,
  session,
}: {
  children: React.ReactNode
  locale: string
  direction: "ltr" | "rtl"
  messages: React.ComponentProps<typeof NextIntlClientProvider>["messages"]
  storeConfig: StoreConfigContextValue
  googleTag: GoogleTagConfig | null
  session: Customer | null
}) {
  // One QueryClient per browser session (useState so it survives re-renders but is
  // never shared across requests on the server).
  const [queryClient] = React.useState(() => new QueryClient())

  return (
    <ThemeProvider colorMode={storeConfig.resolved.colorMode}>
      <QueryClientProvider client={queryClient}>
        <DirectionProvider dir={direction}>
          <NextIntlClientProvider locale={locale} messages={messages}>
            <StoreConfigProvider value={storeConfig}>
              <AnalyticsProvider tag={googleTag}>
                <AuthProvider initialSession={session}>
                  <CartProvider>{children}</CartProvider>
                </AuthProvider>
                <GoogleTag />
                <ConsentBanner />
              </AnalyticsProvider>
            </StoreConfigProvider>
          </NextIntlClientProvider>
        </DirectionProvider>
      </QueryClientProvider>
    </ThemeProvider>
  )
}
